import type { FilterConfigModalItem } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { FiltersConfig } from '@rocco/ui/filter-sort/types/filter-config-section';
import { isFilterStateActive } from '@rocco/ui/filter-sort/utils/get-active-filters';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import {
    isCategoryFilterState,
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchQueryFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { MultipleValuesFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/multiple-values-filter-state';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';
import type {
    FilterStates,
    FilterStatesKey,
} from '@rocco/ui/filter-sort/view-models/filter-states';
import { getColorItemName } from '@rocco/ui/filter-sort/utils/color-swatch';
import { formatToThousandsDelimiter } from '@rocco/utils/format-number';

export interface RefineChip {
    /** Stable identity for rendering: the filter key, plus the item value for list filters. */
    id: string;
    filterKey: FilterStatesKey;
    label: string;
    /** The chip belongs to a runtime facet filter rather than a static one. */
    isFacet: boolean;
    /** The filter's next state with this chip's value removed. */
    removedState: FilterState;
}

const findConfigItem = (
    config: FiltersConfig,
    filterKey: string,
): FilterConfigModalItem | undefined => {
    for (const section of config.modalFilters) {
        const match = section.filters.find(
            (filter) => filter.filterKey === filterKey,
        );
        if (match) return match;
    }
    return undefined;
};

/**
 * Display label for a selected list item. Colour items carry their swatch
 * CSS in `label` and their name in `value`, so they read from the value.
 */
export const getListItemLabel = (
    item: { label: string | null; value: string | number },
    configItem: FilterConfigModalItem | undefined,
): string =>
    configItem?.type === 'color'
        ? getColorItemName(item.value)
        : (item.label ?? String(item.value));

/**
 * Human-readable summary of a staged range, e.g. "Price range: $1,200–$5,000"
 * or "Length: 10–200 mm". Shared by the chips row and expander summaries.
 */
export const getRangeLabel = (
    filterKey: string,
    state: { value: { min: number; max: number }; rangeDivisor: number },
    configItem: FilterConfigModalItem | undefined,
): string => {
    const divisor = state.rangeDivisor > 0 ? state.rangeDivisor : 1;
    const isPrice = configItem?.type === 'price';
    // Prices read in whole dollars, snapped outward like the drawer's own
    // inputs — a legacy URL can still carry cents, and the chip must not.
    const min = formatToThousandsDelimiter(
        isPrice
            ? Math.floor(state.value.min / divisor)
            : state.value.min / divisor,
    );
    const max = formatToThousandsDelimiter(
        isPrice
            ? Math.ceil(state.value.max / divisor)
            : state.value.max / divisor,
    );
    const name = configItem?.label ?? filterKey;
    const prefix = configItem?.type === 'price' ? '$' : '';
    const unit =
        configItem?.type === 'simpleRange' && configItem.unit
            ? ` ${configItem.unit}`
            : '';

    return `${name}: ${prefix}${min}–${prefix}${max}${unit}`;
};

const getLocationChipLabel = (selectedLocation: {
    placeName?: string;
    fullAddress?: string;
    city?: string;
    region?: string;
    state?: string;
    country?: string;
}): string =>
    selectedLocation.placeName ??
    selectedLocation.fullAddress ??
    selectedLocation.city ??
    selectedLocation.region ??
    selectedLocation.state ??
    selectedLocation.country ??
    'Location';

/**
 * The list state with one selected value removed, built immutably. The
 * shared toggle helpers are deliberately not used here: they mutate the
 * live items array in place, and this derivation runs during render — the
 * mutation silently unchecked the option pills the sheet was still
 * displaying.
 */
const removeListItem = <V, T extends MultipleValuesFilterState<V>>(
    state: T,
    item: SingleValueFilterState<V>,
): T => ({
    ...state,
    items: state.items.map((entry) =>
        entry.value === item.value ? { ...entry, checked: false } : entry,
    ),
    selectedItems: state.selectedItems.filter(
        (entry) => entry.value !== item.value,
    ),
});

/**
 * Derive one chip per selected value across all staged filters, in config
 * order for static filters followed by facet filters. Sort, search-query and
 * category states never produce chips — sort is not a refinement, and the
 * category has its own tree UI. Pure: never mutates the input states.
 */
export const getRefineChips = (
    filterStates: FilterStates,
    config: FiltersConfig,
): RefineChip[] => {
    const chips: RefineChip[] = [];

    for (const [filterKey, state] of Object.entries(filterStates.states)) {
        if (!state || filterKey === 'search') continue;
        if (isSearchQueryFilterState(state) || isCategoryFilterState(state)) {
            continue;
        }

        // Searchable lists extend multiple-values states, so one branch
        // covers both; removal is the same immutable item drop.
        if (
            isSearchableListFilterState(state) ||
            isMultipleValuesFilterState(state)
        ) {
            const configItem = findConfigItem(config, filterKey);
            for (const item of state.selectedItems) {
                chips.push({
                    id: `${filterKey}:${String(item.value)}`,
                    filterKey,
                    label: getListItemLabel(item, configItem),
                    isFacet: false,
                    removedState: removeListItem(state, item),
                });
            }
            continue;
        }

        if (isSingleValueFilterState(state)) {
            if (!state.checked) continue;
            const configItem = findConfigItem(config, filterKey);
            chips.push({
                id: filterKey,
                filterKey,
                label: configItem?.label ?? state.label ?? filterKey,
                isFacet: false,
                removedState: { ...state, checked: false },
            });
            continue;
        }

        if (isRangeFilterState(state)) {
            if (!isFilterStateActive(state)) continue;
            chips.push({
                id: filterKey,
                filterKey,
                label: getRangeLabel(
                    filterKey,
                    state,
                    findConfigItem(config, filterKey),
                ),
                isFacet: false,
                removedState: { ...state, value: { ...state.range } },
            });
            continue;
        }

        if (isLocationFilterState(state)) {
            // Pages can seed a non-null but empty selected location; only a
            // real selection (same activity test as the count badge) chips.
            if (!state.selectedLocation || !isFilterStateActive(state)) {
                continue;
            }
            chips.push({
                id: filterKey,
                filterKey,
                label: getLocationChipLabel(state.selectedLocation),
                isFacet: false,
                removedState: { ...state, selectedLocation: null },
            });
        }
    }

    for (const facet of filterStates.facet?.facetOptions ?? []) {
        for (const item of facet.state.selectedItems) {
            chips.push({
                id: `${facet.id}:${String(item.value)}`,
                filterKey: facet.id,
                label: item.label ?? String(item.value),
                isFacet: true,
                removedState: removeListItem(facet.state, item),
            });
        }
    }

    return chips;
};
